/**
 * Imaging Viewer — a reusable DICOM-style viewer that hosts the pin
 * layer and (in Conversation mode) the contextual thread panel. The
 * pin/comment surface is the only writable state — everything else
 * comes from the workbook.
 *
 * IMPORTANT: this viewer never fabricates a clinically specific
 * anatomical image. When no real asset exists the canvas renders a
 * DICOM-style placeholder frame that shows the workbook filename and
 * metadata so viewers immediately understand what is being previewed
 * without being misled by a fake scan.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  MapPin,
  MessagesSquare,
  PenLine,
  Contrast,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Send,
  AtSign,
  Trash2,
  Check,
  RotateCcw,
  X,
} from "lucide-react";
import {
  getImaging,
  getCaseById,
  getPerson,
  getCollaborationByContext,
} from "@/data/repository";
import { useNi2DemoStore } from "@/context/Ni2DemoStore";
import Ni2MentionMenu from "@/components/ni2/Ni2MentionMenu";
import { formatCaseDate } from "@/data/viewModels";
import { cn } from "@/lib/utils";

const CURRENT_USER_ID = "ATT-001"; // Dr. Maya Rao (demo current user)

const TOOLS = [
  { key: "pin",  label: "Pin",          Icon: MapPin,         enabled: true },
  { key: "conv", label: "Conversation", Icon: MessagesSquare, enabled: true },
  { key: "draw", label: "Draw",         Icon: PenLine,        enabled: false, soon: "Coming soon" },
  { key: "invert", label: "Invert",     Icon: Contrast,       enabled: false, soon: "Coming soon" },
  { key: "3d",   label: "3D",           Icon: Boxes,          enabled: false, soon: "Coming soon" },
];

export default function ImagingViewer({
  imagingId,
  demoFile,        // optional demo-uploaded file record
  onBack,
  onCollapse,
}) {
  // Resolve either a workbook Imaging record or a session-only file
  const workbookRec = useMemo(
    () => (imagingId ? getImaging(imagingId) : null),
    [imagingId]
  );
  const rec = workbookRec || demoFile || null;
  const caseId = rec?.caseId;
  const caseRecord = useMemo(() => (caseId ? getCaseById(caseId) : null), [caseId]);

  // Tool state
  const [tool, setTool] = useState("pin");
  // Slice scrubber — synthetic since no real DICOM series
  const [slice, setSlice] = useState(20);
  const totalSlices = 40;

  const canvasRef = useRef(null);
  const demoStore = useNi2DemoStore();

  const pins = demoStore.getPinsForImaging(imagingId);
  const workbookComments = useMemo(
    () => (imagingId ? getCollaborationByContext("Imaging", imagingId) : []),
    [imagingId]
  );
  const demoComments = demoStore.getDemoCommentsForContext("Imaging", imagingId);
  const allComments = useMemo(
    () => [...workbookComments, ...demoComments],
    [workbookComments, demoComments]
  );

  const [activePinId, setActivePinId] = useState(null);

  const commentsForActivePin = useMemo(
    () => allComments.filter((c) => c.pinId === activePinId),
    [allComments, activePinId]
  );

  // Placing a pin
  const handleCanvasClick = (e) => {
    if (tool !== "pin") return;
    const el = canvasRef.current;
    if (!el || !imagingId) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    const pin = demoStore.createDemoPin({
      imagingId,
      caseId,
      x, y,
      authorPersonId: CURRENT_USER_ID,
    });
    setActivePinId(pin.pinId);
  };

  if (!rec) {
    return (
      <div className="grid h-full place-items-center px-6">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-center">
          <div className="text-[13px] text-[hsl(var(--nn-ink))]">
            Unable to load imaging record.
          </div>
        </div>
      </div>
    );
  }

  const fileName =
    rec.filenamePlaceholder ||
    rec.fileName ||
    `${imagingId || "FILE"}.png`;

  return (
    <div className="flex h-full flex-col" data-testid="ni2-imaging-viewer">
      {/* Header */}
      <header className="flex shrink-0 items-start justify-between gap-3 px-6 pb-3 pt-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onCollapse || onBack}
            className="nn-icon-btn"
            aria-label="Back"
            data-testid="ni2-viewer-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
          </button>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight text-white">
              {fileName}
            </div>
            <div className="mt-0.5 flex items-center gap-3 text-[10.5px] font-mono text-[hsl(var(--nn-ink-muted))]">
              <span>{rec.modality || "IMG"} · {rec.sequence || rec.label || "—"}</span>
              <span>·</span>
              <span>{formatCaseDate(rec.studyDate)}</span>
              <span>·</span>
              <span className="font-mono">{imagingId || rec.fileId}</span>
            </div>
            <div className="mt-0.5 text-[11px] text-[hsl(var(--nn-ink-muted))]">
              {caseRecord?.patientLabel || caseId} · {caseRecord?.diagnosis || ""}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="nn-btn"
          data-testid="ni2-viewer-to-folder"
        >
          <ArrowLeft className="h-3 w-3" strokeWidth={1.8} />
          Back to folder
        </button>
      </header>

      {/* Toolbar */}
      <div className="flex shrink-0 items-center gap-2 border-y border-white/[0.05] bg-white/[0.02] px-6 py-2.5">
        <span className="mr-1 nn-eyebrow" style={{ letterSpacing: "0.14em" }}>
          {rec.demo || rec.source === "demo" ? "DEMO" : "DICOM"}
        </span>
        <span className="mr-2 font-mono text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
          {fileName}
        </span>
        <div className="ml-auto flex items-center gap-2" data-testid="ni2-viewer-toolbar">
          {TOOLS.map(({ key, label, Icon, enabled, soon }) => (
            <button
              key={key}
              type="button"
              disabled={!enabled}
              onClick={() => enabled && setTool(key)}
              className="nn-viewer-toolbar-btn"
              data-active={tool === key || undefined}
              data-disabled={!enabled || undefined}
              data-testid={`ni2-viewer-tool-${key}`}
              title={enabled ? label : soon}
              aria-label={label}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
              {label}
              {!enabled && (
                <span className="ml-1 rounded-full border border-white/[0.10] px-1.5 text-[8.5px] font-mono uppercase tracking-[0.14em]">
                  soon
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main body: viewer + optional thread panel */}
      <div className="min-h-0 flex-1 grid gap-4 px-6 py-4" style={{
        gridTemplateColumns: tool === "conv" ? "minmax(0, 1fr) 320px" : "1fr",
      }}>
        <div className="flex min-h-0 flex-col">
          <div
            ref={canvasRef}
            className="nn-viewer-canvas relative flex-1"
            onClick={handleCanvasClick}
            data-testid="ni2-viewer-canvas"
            style={{ cursor: tool === "pin" ? "crosshair" : "default", minHeight: 360 }}
          >
            <PlaceholderScan fileName={fileName} modality={rec.modality} sequence={rec.sequence} />

            {/* DICOM-style overlays */}
            <div className="nn-viewer-overlay" style={{ top: 10, left: 12 }}>
              SER {String(slice % 8 + 1).padStart(2, "0")}
              <br />
              IMG {String(slice).padStart(2, "0")}/{totalSlices}
              <br />
              {rec.modality || "IMG"}
            </div>
            <div className="nn-viewer-overlay" style={{ top: 10, right: 12, textAlign: "right" }}>
              TR/TE 3000/100
              <br />
              2.5 mm
              <br />
              {rec.sequence || rec.label || "—"}
            </div>
            <div className="nn-viewer-overlay" style={{ bottom: 10, left: 12 }}>
              W 340
              <br />
              L 128
            </div>
            <div className="nn-viewer-overlay" style={{ bottom: 10, right: 12, textAlign: "right" }}>
              1.0×
              <br />
              5 cm
            </div>

            {/* Pins */}
            {pins.map((p) => {
              const thread = allComments.filter((c) => c.pinId === p.pinId);
              return (
                <button
                  key={p.pinId}
                  type="button"
                  className="nn-pin"
                  data-active={activePinId === p.pinId || undefined}
                  data-testid={`ni2-pin-${p.pinId}`}
                  aria-label={`Pin ${p.pinId}`}
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePinId(p.pinId);
                    if (tool !== "conv") setTool("conv");
                  }}
                >
                  {thread.length > 0 && (
                    <span
                      className="absolute -right-1 -top-1 grid h-3 w-3 place-items-center rounded-full border border-white/40 bg-[hsl(var(--nn-blue))] text-[8px] font-semibold text-white"
                    >
                      {thread.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Slice scrubber */}
          <div className="mt-3 flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.03] px-3 py-2">
            <button
              type="button"
              onClick={() => setSlice((s) => Math.max(1, s - 1))}
              className="nn-icon-btn h-7 w-7"
              aria-label="Previous slice"
              data-testid="ni2-viewer-prev-slice"
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
            <input
              type="range"
              min={1}
              max={totalSlices}
              value={slice}
              onChange={(e) => setSlice(Number(e.target.value))}
              className="nn-viewer-slider flex-1"
              data-testid="ni2-viewer-slice-slider"
              aria-label="Slice"
            />
            <button
              type="button"
              onClick={() => setSlice((s) => Math.min(totalSlices, s + 1))}
              className="nn-icon-btn h-7 w-7"
              aria-label="Next slice"
              data-testid="ni2-viewer-next-slice"
            >
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
            <span className="font-mono text-[11px] text-[hsl(var(--nn-ink-muted))]">
              {String(slice).padStart(2, "0")} / {totalSlices}
            </span>
          </div>

          {/* Metadata strip */}
          <div className="mt-3 grid grid-cols-4 gap-2">
            <MetaCell label="Key finding" value={rec.keyFinding || "—"} span={4} />
            {rec.annotationSummary && (
              <MetaCell label="Annotation" value={rec.annotationSummary} span={4} />
            )}
          </div>
        </div>

        {/* Thread panel (only in Conversation mode) */}
        {tool === "conv" && (
          <ContextThread
            imagingId={imagingId}
            caseId={caseId}
            activePinId={activePinId}
            allComments={allComments}
            commentsForActivePin={commentsForActivePin}
            pins={pins}
            onSelectPin={setActivePinId}
            onCreatePin={() => {
              // Nudge user to switch to pin tool if none is selected yet
              setTool("pin");
            }}
            onRemovePin={(id) => {
              demoStore.removeDemoPin(id);
              if (activePinId === id) setActivePinId(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

/* -------------------- Placeholder scan (no fake anatomy) -------------------- */
function PlaceholderScan({ fileName, modality, sequence }) {
  return (
    <div className="pointer-events-none absolute inset-0">
      {/* subtle brain-envelope silhouette using CSS gradients — abstract,
          not a synthetic anatomical scan. */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          transform: "translate(-50%, -50%)",
          width: "62%",
          aspectRatio: "1 / 1",
          borderRadius: "42% 46% 44% 44% / 48% 42% 48% 44%",
          background:
            "radial-gradient(ellipse at 40% 40%, rgba(180,190,220,0.10) 0%, rgba(140,160,200,0.06) 42%, rgba(60,80,120,0.02) 72%, transparent 100%)",
          boxShadow:
            "inset 0 0 60px rgba(255,255,255,0.05), inset 0 0 140px rgba(0,0,0,0.55)",
        }}
      />
      <div
        className="absolute left-1/2 top-1/2 opacity-40"
        style={{
          transform: "translate(-50%, -50%)",
          width: "36%",
          aspectRatio: "1 / 1",
          borderRadius: "50% 50% 50% 50% / 46% 46% 54% 54%",
          background:
            "radial-gradient(ellipse at 50% 45%, rgba(230,236,255,0.12), transparent 68%)",
          filter: "blur(6px)",
        }}
      />
      {/* No-preview watermark so viewers know this is a placeholder */}
      <div
        className="absolute left-1/2"
        style={{ bottom: "8%", transform: "translateX(-50%)" }}
      >
        <div
          className="rounded-full border border-white/[0.08] bg-black/25 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/40 backdrop-blur"
        >
          Preview placeholder · no live asset · {modality || "IMG"}{sequence ? ` · ${sequence}` : ""}
        </div>
      </div>
    </div>
  );
}

function MetaCell({ label, value, span = 1 }) {
  return (
    <div
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2"
      style={{ gridColumn: `span ${span}` }}
    >
      <div className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        {label}
      </div>
      <div className="mt-0.5 text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink))]">
        {value}
      </div>
    </div>
  );
}

/* -------------------- Context Thread (Ni² sidebar) -------------------- */

function ContextThread({
  imagingId,
  caseId,
  activePinId,
  allComments,
  commentsForActivePin,
  pins,
  onSelectPin,
  onCreatePin,
  onRemovePin,
}) {
  const demoStore = useNi2DemoStore();
  const [draft, setDraft] = useState("");
  const [mentions, setMentions] = useState([]); // [{personId, name}]
  const [mentionQuery, setMentionQuery] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft("");
    setMentions([]);
    setMentionQuery(null);
  }, [activePinId, imagingId]);

  // Detect "@..." at end of draft to open mention menu
  const handleChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    const m = /@([\w-]{0,24})$/.exec(v);
    if (m) setMentionQuery(m[1]);
    else setMentionQuery(null);
  };

  const handlePickMention = (person) => {
    // Replace trailing "@xxx" with "@FullName "
    setDraft((prev) => prev.replace(/@([\w-]{0,24})$/, `@${person.fullName} `));
    setMentions((prev) =>
      prev.find((p) => p.personId === person.personId)
        ? prev
        : [...prev, { personId: person.personId, name: person.fullName }]
    );
    setMentionQuery(null);
    inputRef.current?.focus();
  };

  const submit = () => {
    const text = draft.trim();
    if (!text || !activePinId) return;
    demoStore.createDemoImagingComment({
      contextType: "Imaging",
      contextId: imagingId,
      caseId,
      pinId: activePinId,
      threadId: `THREAD-${activePinId}`,
      authorPersonId: CURRENT_USER_ID,
      mentionedPersonIds: mentions.map((m) => m.personId),
      comment: text,
    });
    setDraft("");
    setMentions([]);
    setMentionQuery(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !mentionQuery) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <aside
      className="nn-glass-inset flex min-h-0 flex-col overflow-hidden"
      data-testid="ni2-context-thread"
      style={{ maxHeight: "100%" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-white/[0.05] px-4 py-3">
        <div className="min-w-0">
          <div className="nn-eyebrow">Ni² · Imaging thread</div>
          <div className="mt-0.5 truncate text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
            {activePinId ? `Pin ${activePinId}` : "Select or drop a pin"}
          </div>
        </div>
        {pins.length > 0 && (
          <span className="nn-chip">{pins.length} pin{pins.length === 1 ? "" : "s"}</span>
        )}
      </div>

      {/* Pin selector row */}
      {pins.length > 0 && (
        <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-white/[0.04] px-3 py-2">
          {pins.map((p, i) => (
            <button
              key={p.pinId}
              type="button"
              onClick={() => onSelectPin(p.pinId)}
              className={cn(
                "shrink-0 rounded-full border px-2.5 py-1 text-[10.5px] font-mono transition",
                activePinId === p.pinId
                  ? "border-[hsl(var(--nn-blue))] bg-[rgba(160,200,255,0.14)] text-white"
                  : "border-white/[0.08] bg-white/[0.03] text-[hsl(var(--nn-ink-muted))] hover:bg-white/[0.06]"
              )}
              data-testid={`ni2-pin-chip-${p.pinId}`}
            >
              #{i + 1} · {p.pinId.slice(-4)}
            </button>
          ))}
        </div>
      )}

      {/* Comment list */}
      <div className="nn-scroll min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {!activePinId && (
          <div className="rounded-xl border border-dashed border-white/[0.10] bg-white/[0.02] p-4 text-center">
            <div className="text-[12px] font-medium text-[hsl(var(--nn-ink))]">
              Drop a pin on the image
            </div>
            <div className="mt-1 text-[11px] text-[hsl(var(--nn-ink-muted))]">
              Click anywhere on the scan with Pin mode active. Ni² will attach every comment you
              send to that pin.
            </div>
          </div>
        )}

        {activePinId && commentsForActivePin.length === 0 && (
          <div className="text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
            No comments on this pin yet. @-mention a colleague and send your first note below.
          </div>
        )}

        {commentsForActivePin.map((c) => (
          <CommentCard key={c.collaborationId} c={c} />
        ))}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-white/[0.05] p-3">
        <div className="relative">
          {mentionQuery !== null && (
            <Ni2MentionMenu
              query={mentionQuery}
              onPick={handlePickMention}
              onClose={() => setMentionQuery(null)}
            />
          )}
          <div
            className={cn(
              "flex items-end gap-2 rounded-2xl border border-white/[0.10] bg-white/[0.03] px-2.5 py-2",
              !activePinId && "opacity-50"
            )}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              rows={2}
              placeholder={
                activePinId ? "Ask this document…  Try @ to mention a colleague" : "Select a pin to comment"
              }
              disabled={!activePinId}
              className="min-h-[38px] flex-1 resize-none bg-transparent px-1 py-1 text-[12.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] outline-none"
              data-testid="ni2-thread-input"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!activePinId || !draft.trim()}
              className="nn-icon-btn shrink-0 disabled:opacity-40"
              aria-label="Send"
              data-testid="ni2-thread-send"
              style={{
                background:
                  "linear-gradient(180deg, rgba(160,200,255,0.28), rgba(120,165,240,0.14))",
                borderColor: "rgba(160,200,255,0.42)",
                color: "hsl(var(--nn-ink))",
              }}
            >
              <Send className="h-3.5 w-3.5" strokeWidth={1.6} />
            </button>
          </div>
          {mentions.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {mentions.map((m) => (
                <span
                  key={m.personId}
                  className="inline-flex items-center gap-1 rounded-full border border-white/[0.10] bg-white/[0.06] px-2 py-0.5 text-[10.5px] text-[hsl(var(--nn-ink))]"
                >
                  <AtSign className="h-2.5 w-2.5" strokeWidth={2} />
                  {m.name}
                  <span className="font-mono text-[9.5px] text-[hsl(var(--nn-ink-faint))]">
                    {m.personId}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setMentions((prev) => prev.filter((x) => x.personId !== m.personId))
                    }
                    className="ml-0.5 rounded-full p-0.5 text-[hsl(var(--nn-ink-faint))] hover:bg-white/[0.08]"
                    aria-label={`Remove ${m.name}`}
                  >
                    <X className="h-2.5 w-2.5" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {activePinId && (
            <div className="mt-1.5 flex items-center justify-between px-1 text-[10px] text-[hsl(var(--nn-ink-faint))]">
              <span>
                Attaches to Imaging {imagingId} · Case {caseId} · Pin {activePinId.slice(-6)}
              </span>
              <button
                type="button"
                onClick={() => onRemovePin(activePinId)}
                className="flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-white/[0.06] hover:text-[hsl(var(--nn-warn))]"
                data-testid="ni2-thread-delete-pin"
                title="Delete pin and its thread"
              >
                <Trash2 className="h-2.5 w-2.5" strokeWidth={1.8} />
                Delete pin
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function CommentCard({ c }) {
  const demoStore = useNi2DemoStore();
  const author = getPerson(c.authorPersonId);
  const isResolved = c.resolved === "Yes";
  const isDemo = c.demo === true;

  const highlightedText = useMemo(() => {
    // wrap "@Name" tokens with an accent chip
    const text = String(c.comment || "");
    return text.split(/(@[\w'\- ]+?(?=[\s,.!?]|$))/g);
  }, [c.comment]);

  return (
    <div
      className="mb-2 rounded-2xl border border-white/[0.06] bg-white/[0.03] p-3"
      data-testid={`ni2-thread-comment-${c.collaborationId}`}
    >
      <div className="flex items-center gap-2 text-[10.5px]">
        <span className="font-medium text-[hsl(var(--nn-ink))]">
          {author?.fullName || c.authorPersonId}
        </span>
        <span className="font-mono text-[9.5px] text-[hsl(var(--nn-ink-faint))]">
          {String(c.dateAndTime || "").slice(0, 16).replace("T", " ")}
        </span>
        {isDemo && (
          <span className="ml-auto rounded-full border border-white/[0.08] bg-white/[0.04] px-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
            demo
          </span>
        )}
      </div>
      <p className="mt-1 text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">
        {highlightedText.map((chunk, i) =>
          chunk.startsWith("@") ? (
            <span
              key={i}
              className="rounded-md bg-[rgba(160,200,255,0.15)] px-1 text-[hsl(var(--nn-blue))]"
            >
              {chunk}
            </span>
          ) : (
            <span key={i}>{chunk}</span>
          )
        )}
      </p>
      <div className="mt-2 flex items-center justify-between text-[10.5px]">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
            isResolved
              ? "border border-[hsl(var(--nn-ok))]/40 bg-[hsl(var(--nn-ok))]/10 text-[hsl(var(--nn-ok))]"
              : "border border-white/[0.08] bg-white/[0.03] text-[hsl(var(--nn-ink-muted))]"
          )}
        >
          {isResolved ? <Check className="h-2.5 w-2.5" strokeWidth={2} /> : null}
          {isResolved ? "Resolved" : "Open"}
        </span>
        {isDemo && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => demoStore.setCommentResolved(c.collaborationId, !isResolved)}
              className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[hsl(var(--nn-ink-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--nn-ink))]"
              data-testid={`ni2-thread-toggle-resolve-${c.collaborationId}`}
            >
              {isResolved ? <RotateCcw className="h-2.5 w-2.5" strokeWidth={1.8} /> : <Check className="h-2.5 w-2.5" strokeWidth={1.8} />}
              {isResolved ? "Reopen" : "Resolve"}
            </button>
            <button
              type="button"
              onClick={() => demoStore.deleteComment(c.collaborationId)}
              className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[hsl(var(--nn-ink-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--nn-warn))]"
              aria-label="Delete comment"
              data-testid={`ni2-thread-delete-${c.collaborationId}`}
            >
              <Trash2 className="h-2.5 w-2.5" strokeWidth={1.8} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
